import moment from 'moment';

import config from '../../config';
import callApi from '../../util/apiCaller';
import { sequence } from '../../util/promises';
import { formatPrice, getPriceCent, getPriceFromCent } from '../../util/price';

import { getApplicantEvent, getApplicantOption, getApplicantSelectedQuestions } from '../Applicant/ApplicantActions';
import { getSubjectOption } from '../Subject/SubjectActions';
import { getPageOption } from '../Page/PageActions';
import { getCredit } from '../Institution/CreditActions';
import { sendSocket, setIsFetching, FETCH_LIMIT, getItemsRequest } from '../App/AppActions';
import { addError } from '../Error/ErrorActions';

// Export Constants
export const SET_SESSIONS = 'SET_SESSIONS';
export const SET_SESSION_SUBJECT_DATA = 'SET_SESSION_SUBJECT_DATA';
export const ADD_ACTIVE_PROCTOR = 'ADD_ACTIVE_PROCTOR';
export const REMOVE_ACTIVE_PROCTOR = 'REMOVE_ACTIVE_PROCTOR';
export const REMOVE_SESSION = 'REMOVE_SESSION';

export const SESSION_STATUS_MODEL = 'model';
export const SESSION_STATUS_PENDING = 'pending';
export const SESSION_STATUS_ONPROGRESS = 'onprogress';
export const SESSION_STATUS_DONE = 'done';
export const SESSION_STATUS_ONPROCESS = 'onprocess';
export const SESSION_STATUS_ONCORRECTION = 'oncorrection';
export const SESSION_STATUS_CORRECTED = 'corrected';
export const SESSION_STATUS_ARCHIVED = 'archived';
export const SESSION_STATUS_BLOCKED = 'blocked';

export const SESSION_TEST_APPLICANT_COUNT = 5;

export const SESSION_DONE_DELAY_IN_HOURS = 2;
export const SESSION_CORRECT_DELAY_IN_MONTHS = 1;
export const SESSION_ARCHIVE_DELAY_IN_MONTHS = 1;
export const SESSION_DELETE_DELAY_IN_MONTHS = 8;

export function getSessionsCountRequest(search = '', filters = {}) {
	return dispatch => {
		return callApi(`sessions/count?${search ? `search=${search}&` : ''}${filters ? `${Object.keys(filters).map(filterKey => `filters[${filterKey}]=${filters[filterKey]}`).join('&')}&` : ''}`).then(result => {
			return result.count;
		}).catch(err => {
			return null;
		});
	};
}

export function getSessionRequest(sessionId) {
	if(sessionId) {
		return dispatch => {
			dispatch(setIsFetching('sessions'));
			return callApi(`session/${sessionId}`).then(result => {
				dispatch(setSessions([result.session], false));
				return result.session;
			}).catch(err => {
				return null;
			});
		};
	}
	return dispatch => Promise.resolve(null);
}

export function getSessionsRequest(search = '', filters = null, sorter = null, pager = { current: 0, size: FETCH_LIMIT }, extra = {}, options = {}) {
	return dispatch => dispatch(getItemsRequest('sessions', null, items => dispatch(setSessions(items || [], false)), { search, filters, sorter, pager, extra }, options));
}

export function getUserSessionsRequest(userId) {
	return dispatch => dispatch(getItemsRequest(`user/${userId}/sessions`, 'sessions', null, { pager: 10 } /* , { isRecursive: true } */));
	// return dispatch => {
	//     return callApi(`user/${userId}/sessions`).then(result => {
	//         return result && result.sessions;
	//     }).catch(err => {
	//         return null;
	//     });
	// };
}

export function getSessionSubjectDataRequest(sessionId) {
	return dispatch => {
		return callApi(`session/${sessionId}/subject`).then(result => {
			result.subjectData && dispatch(setSessionSubjectData(sessionId, result.subjectData));
			return result.subjectData;
		}).catch(err => {
			return null;
		});
	};
}

export function editSessionRequest(session) {
	return dispatch => {
		return callApi('session/edit', 'post', { session }).then(result => {
			if(result.session) {
				dispatch(getSessionRequest(result.session._id));
				dispatch(sendSocket({ type: 'session/update', data: { session: result.session._id, institution: result.session.institution, sendToApplicant: !!session._id } }));
			}
			return result.session;
		}).catch(err => {
			return null;
		});
	};
}

export function publishRequest(session, selection, senderName, customText, convocationDate, forceReset, noEmailSend, forceSend) {
	return dispatch => {
		return callApi('session/publish', 'post', {
			session: { _id: session._id },
			selection,
			senderName,
			customText,
			convocationDate,
			forceReset,
			noEmailSend,
			forceSend,
		}).then(result => {
			return result.ok;
		}).catch(err => {
			return null;
		});
	};
}

export function sendConvocationPreviewRequest(session, senderName, customText, convocationDate) {
	return callApi('session/convocation/preview', 'post', {
		session: { _id: session._id },
		senderName,
		customText,
		convocationDate,
	}).then(result => {
		return result.ok;
	}).catch(err => {
		return null;
	});
}

export function addProctorTrackRequest(session, proctor, track) {
	return dispatch => {
		return callApi('session/proctor/addtrack', 'post', { session: { _id: session._id }, proctor: { _id: proctor._id }, track }).then(res => {
			return res.session;
		}).catch(error => {
			console.error(error);
			return null;
		});
	};
}

export function validCorrectionRequest(session) {
	return dispatch => {
		return callApi('session/correct', 'post', { session: { _id: session._id } }).then(result => {
			result.session && dispatch(getSessionRequest(session._id));
			return result.session;
		}).catch(err => {
			return null;
		});
	};
}

export function setSessionsApplicantsCountRequest() {
	return dispatch => {
		return callApi('sessions/applicant/setcount').then(result => {
			// result.ok && dispatch(getSessionsRequest());
			return result;
		}).catch(err => {
			return null;
		});
	};
}

export function askSessionExportRequest(sessionId, user = null, options = {}) {
	return dispatch => {
		return callApi(`session/${sessionId}/export`, 'post', { user, options }).then(result => {
			return result.ok;
		}).catch(err => {
			return null;
		});
	};
}

export function closeSessionRequest(sessionId) {
	return dispatch => {
		return callApi('session/close', 'post', { session: { _id: sessionId } }).then(result => {
			dispatch(getSessionRequest(sessionId));
			return result.ok;
		}).catch(err => {
			return null;
		});
	};
}
export function resetSessionSubjectRequest(sessionId, resetCorrections = false) {
	return dispatch => {
		return callApi(`session/${sessionId}/subject/reset`, 'post', { resetCorrections }).then(result => {
			return result.ok;
		}).catch(err => {
			return null;
		});
	};
}

export function removeSessionRequest(sessionId) {
	return dispatch => {
		return callApi('session/remove', 'delete', { session: { _id: sessionId } }).then(result => {
			// dispatch(getSessionRequest(sessionId));
			result.ok && dispatch(removeSession(sessionId));
			return result.ok;
		}).catch(err => {
			return null;
		});
	};
}

export function hasOnProgressSession(subjectId) {
	return dispatch => {
		if(subjectId) {
			return dispatch(getSessionsRequest('', { status: 'onprogress', subject: subjectId }, null, { size: 1 })).then(sessions => {
				return (sessions || []).length > 0;
			});
		}
		return Promise.resolve(false);
	};
}

export function getSessionsLTIDeepLinkRessourceFormData(sessionIds) {
	return callApi(`lti/deeplink/response?sessions=${sessionIds.join(',')}`);
}

// Getters
export function getSessions(store, statuses = null) {
	let sessions = store.sessions.data;
	if(statuses !== null) {
		if(!Array.isArray(statuses)) {
			statuses = [statuses];
		}
		sessions = sessions.filter(session => statuses.includes(session.status));
	}
	return sessions;
}

export function getSession(store, id) {
	return store.sessions.data.find(session => session._id === id);
}

export function getSessionByCode(store, code) {
	return store.sessions.data.find(session => session.code === code);
}

export function getActiveProctors(store, sessionId) {
	return (getSession(store, sessionId) || {}).activeProctors || [];
}

export function getProctorActivityIndex(store, sessionId) {
	return (getSession(store, sessionId) || {}).proctorActivityIndex || 0;
}

export function getSessionId(session) {
	return (session && session._id) || session;
}

export function getSessionInstitutionId(session) {
	return (session.institution && session.institution._id) || session.institution;
}

export function getSessionCreatorId(session) {
	return (session.creator && session.creator._id) || session.creator;
}

export function getSessionProctorsIds(session) {
	return (session.proctors || []).map(proctor => proctor._id || proctor);
}

export function getSessionCorrectorsIds(session) {
	return (session.correctors || []).map(corrector => corrector._id || corrector);
}

export function canEditSession(session, user) {
	return session && user && (
		(user.institutions.includes(session.institution) || user.institutions.includes('*'))
        && (
        	['root', 'admin', 'manager'].includes(user.role)
            || (
            	isSessionCreator(session, user)
                && !['redactor', 'proctor', 'corrector', 'student'].includes(user.role)
            )
        )
	);
}

export function canProctorSession(session, user) {
	return session && user && (
		(user.institutions.includes(session.institution) || user.institutions.includes('*'))
        && (
        	['root', 'admin', 'manager'].includes(user.role)
            || (
            	isSessionProctor(session, user)
                && !['redactor', 'corrector', 'student'].includes(user.role)
            )
        )
	);
}

export function canCorrectSession(session, user, isForApplicant = false) {
	return session && user && (
		(user.institutions.includes(session.institution) || user.institutions.includes('*'))
        && (
        	['root', 'admin', 'manager'].includes(user.role)
            || (
            	(isForApplicant || [SESSION_STATUS_ONCORRECTION].includes(session.status))
                && (
                	isSessionCorrector(session, user)
                    || isSessionCreator(session, user)
                ) && !['redactor', 'proctor', 'student'].includes(user.role)
            )
        )
	);
}

export function canAddApplicant(user, session, sessions, institution, applicants = []) {
	if(!user || !session || !institution) {
		return false;
	}

	if(user.role === 'root') {
		return true;
	}

	const sessionCreditType = getSessionCreditType(session);

	const pendingSessionsCreditsNeed = [
		...sessions.filter(sessionCredit => ['pending', 'onprogress'].includes(sessionCredit.status) && getSessionInstitutionId(sessionCredit) === institution._id),
		{ ...session, applicantsCount: applicants.length },
	].reduce((need, sessionCredit) => {
		const credit = getCredit(institution, getSessionCreditType(sessionCredit));

		if(credit.isUnlimited) {
			return need;
		}

		const sessionCreditNeedValue = credit.isHourly
			? (sessionCredit.applicantsCount * (getSessionOption(sessionCredit, 'duration') ? getSessionOption(sessionCredit, 'duration') / 60 : moment.duration(moment(sessionCredit.dates.end).diff(moment(sessionCredit.dates.start))).as('hours')))
			: sessionCredit.applicantsCount;

		return need + formatPrice(sessionCreditNeedValue);
	}, 0);

	console.log(getCredit(institution, sessionCreditType), pendingSessionsCreditsNeed);

	const credit = getCredit(institution, sessionCreditType) || {};

	if(credit.isUnlimited) {
		return true;
	}

	return getCredit(institution, sessionCreditType).value > pendingSessionsCreditsNeed;
}

export function isSessionCreator(session, user) {
	return session && user && user._id === getSessionCreatorId(session);
}

export function isSessionProctor(session, user) {
	return session && user && getSessionProctorsIds(session).includes(user._id);
}

export function isSessionCorrector(session, user) {
	return session && user && getSessionCorrectorsIds(session).includes(user._id);
}

export function getSessionDate(session, date) {
	if(session && session.dates) {
		return session.dates[date];
	}
	return '';
}

export function getSessionInformation(session, key) {
	if(session && session.informations) {
		return session.informations[key];
	}
	return '';
}

export function getSessionOption(session, option) {
	if(session && session.options) {
		return session.options[option];
	}
	return '';
}

/*
 * Check if the user gets every applicant of the session, or only the ones he is assigned to.
 * Correctors distribution restricts professors & correctors to their own assignation.
 */
export function isAllowedForEveryApplicant(user, session) {
	if(!['professor', 'corrector'].includes((user || {}).role)) {
		return true;
	}
	return !(getSessionOption(session, 'isDoubleCorrection') || getSessionOption(session, 'useCorrectorsDistribution'));
}

// Effective webcam capture interval in seconds. `webcamCaptureInterval` stores the
// raw value the admin typed and `webcamCaptureIntervalUnit` ('minutes' | 'seconds',
// default 'minutes' for backward compatibility) tells how to interpret it.
export function getWebcamCaptureIntervalSeconds(session) {
	const value = parseFloat(getSessionOption(session, 'webcamCaptureInterval') || config.application.options.webcamCaptureInterval) || 10;
	return getSessionOption(session, 'webcamCaptureIntervalUnit') === 'seconds' ? value : value * 60;
}

// Streams to photograph during a webcam-capture exam. Unlike `liveMedias`, an empty
// or absent value means camera-only (backward compatibility), not "all medias".
export function getWebcamCaptureMedias(session) {
	const medias = getSessionOption(session, 'webcamCaptureMedias');
	return medias?.length ? medias : ['camera'];
}

export function isWebcamCaptureMediaEnabled(session, mediaType) {
	return getWebcamCaptureMedias(session).includes(mediaType);
}

export function getSessionExternal(session, external) {
	if(session && session.external) {
		return session.external[external];
	}
	return '';
}

export function getSessionCreditType(session) {
	// For others multiples types (in future, like "thesis" or "queue" type), use if else if conditions sequence
	return getSessionOption(session, 'isLiveProctoring') ? 'live' : 'exam';
}

export function isSessionLiveMediaEnabled(session, mediaType) {
	return !getSessionOption(session, 'liveMedias')?.length || getSessionOption(session, 'liveMedias').includes(mediaType);
}

export function getSessionStatusColor(status) {
	switch(status) {
	case SESSION_STATUS_ONPROGRESS:
		return 'info';

	case SESSION_STATUS_DONE:
		return 'gray-600';

	case SESSION_STATUS_ONCORRECTION:
		return 'warning';

	case SESSION_STATUS_CORRECTED:
		return 'success';

	case SESSION_STATUS_ARCHIVED:
		return 'secondary';

	case SESSION_STATUS_PENDING:
	default:
		return 'primary';
	}
}

export function getSessionType(session) {
	let type = 'exam';

	if(getSessionOption(session, 'isLiveProctoring')) {
		type = 'live';
		if(getSessionOption(session, 'isLiveQueue')) {
			type += 'queue';
		} else if(getSessionOption(session, 'isLiveThesis')) {
			type += 'thesis';
		}
	} else if(getSessionOption('isExamGroup')) {
		type += 'group';
	}
	return type;
}

export function getSessionTypeIcon(session) {
	switch(getSessionType(session)) {
	case 'exam':
		return 'tasks';

	case 'examgroup':
		return 'users';

	case 'live':
	case 'livequeue':
		return 'video';

	case 'livethesis':
		return 'chalkboard-teacher';

	default:
		return '';
	}
}

/*
 * Pick-up pages & questions selection for applicant depending on subject and pages options
 * Subject options: isPageRandomOrder, pagesCount (si isPageRandomOrder == true), isQuestionRandomOrder, questionsCount (si isQuestionRandomOrder == true)
 */
export function selectPagesAndQuestions(session, subject, pages, questions) {
	if(subject) {
		// Choose only for active pages with questions
		pages = (pages || []).filter(page => page.isActive && page.subject.toString() === subject._id.toString() && (questions || []).some(question => question.page.toString() === page._id.toString()));

		// Choose only for active questions with page associated and label set
		questions = (questions || []).filter(question => question.isActive && question.label && question.subject.toString() === subject._id.toString() && pages.find(page => page._id.toString() === question.page.toString()));

		let pagesSelection = [];
		let questionsSelection = [];
		let iterator = 0;

		// console.log('pages/questions available', pages.length, questions.length);

		// Random pages order
		const isPageRandomOrder = getSessionOption(session, 'rewriteSubjectOptions') ? getSessionOption(session, 'isPageRandomOrder') : getSubjectOption(subject, 'isPageRandomOrder');
		const pagesCount = (getSessionOption(session, 'rewriteSubjectOptions') && getSessionOption(session, 'pagesCount')) || getSubjectOption(subject, 'pagesCount') || pages.length;

		// console.log(`Selection of ${pagesCount} pages ${isPageRandomOrder ? 'randomly' : 'in order'}`);

		isPageRandomOrder && console.log('Pages are randomly selected', pagesCount);
		if(isPageRandomOrder && pagesCount) {
			let pagesReservoir = pages;
			while(pagesSelection.length < pagesCount && iterator < (pagesCount + 100)) {
				const random = Math.floor(Math.random() * pagesReservoir.length);
				const selectedPage = pagesReservoir[random];
				if(selectedPage) {
					pagesSelection.push(selectedPage);
					pagesReservoir = pagesReservoir.filter(page => page._id.toString() !== selectedPage._id.toString());
					// console.log(`Page index selected ${random}/${pagesReservoir.length}`);
				}
				iterator += 1;
			}
		} else { // All pages selected
			pagesSelection = pages.sort((pageA, pageB) => pageA.sort - pageB.sort);
		}
		// console.log('pagesSelection', pagesSelection.length);
		let hasPageWithQuestionRandomOrder = false;

		pagesSelection.forEach(page => {
			iterator = 0;
			let pageQuestionsSelected = [];
			let pageQuestions = questions.filter(question => question.page.toString() === page._id.toString());
			// Random questions order
			const isQuestionRandomOrder = getSessionOption(session, 'rewriteSubjectOptions') ? getSessionOption(session, 'isQuestionRandomOrder') : (getPageOption(page, 'isQuestionRandomOrder') || getSubjectOption(subject, 'isQuestionRandomOrder'));
			const questionsCount = Math.min(pageQuestions.length, (getSessionOption(session, 'rewriteSubjectOptions') && getSessionOption(session, 'questionsCount')) || getPageOption(page, 'questionsCount') || getSubjectOption(subject, 'questionsCount') || questions.length);
			hasPageWithQuestionRandomOrder = hasPageWithQuestionRandomOrder || isQuestionRandomOrder;
			// console.log(`Selection of ${questionsCount} questions for page '${page.label}' ${isQuestionRandomOrder ? 'randomly' : 'in order'}`);

			// isQuestionRandomOrder && console.log(`Questions are randomly selected: ${questionsCount} questions/page`);
			if(isQuestionRandomOrder && questionsCount) {
				if(pageQuestions.length) {
					while(pageQuestionsSelected.length < questionsCount && iterator < (questionsCount + 100)) {
						const random = Math.floor(Math.random() * pageQuestions.length);
						const selectedQuestion = pageQuestions[random];
						if(selectedQuestion) {
							pageQuestionsSelected.push(selectedQuestion);
							pageQuestions = pageQuestions.filter(question => question._id.toString() !== selectedQuestion._id.toString());
						} else {
							console.log('Random selection failed', random, selectedQuestion, page, pageQuestions);
						}
						iterator += 1;
					}
				} else {
					console.log('No questions for this page');
				}
			} else {
				pageQuestionsSelected = questions.filter(question => question.page.toString() === page._id.toString()).sort((questionA, questionB) => questionA.sort - questionB.sort);
			}
			questionsSelection = questionsSelection.concat(pageQuestionsSelected);
		});

		console.log(`Pages&Questions Selection: ${pagesSelection.length} pages / ${questionsSelection.length} questions`);
		return {
			pages: pagesSelection.map(page => page._id.toString()),
			questions: questionsSelection.map(question => {
				if(!question || !(question || {})._id) {
					console.log(question);
				}
				return question._id.toString();
			}),
			isPageRandomOrder,
			isQuestionRandomOrder: hasPageWithQuestionRandomOrder,
		};
	}
	return {};
}

export function getSessionQuestions(subject, applicants) {
	const questions = [];
	(applicants || [])
		.filter(applicant => applicant.selectedQuestions && applicant.selectedQuestions.questions)
		.map(applicant => getApplicantSelectedQuestions(subject, applicant))
		.forEach(questionsList => {
			questionsList.forEach(questionToAdd => {
				!questions.find(question => question._id === questionToAdd._id) && questions.push(questionToAdd);
			});
		});
	return questions;
}

export function getSessionStart(session, applicant, isAccess = false) {
	let sessionStartDate = moment.utc(session?.dates?.start);
	if(!isAccess) {
		sessionStartDate = moment.utc(session?.dates?.identification || session?.dates?.start);
	}
	if(applicant && getSessionOption(session, 'isSlidingSchedule') && getApplicantEvent(applicant, 'access', 'first')) {
		sessionStartDate = moment.utc(getApplicantEvent(applicant, 'access', 'first')?.date);
	} else if(applicant?.options?.timeLagInterval) {
		sessionStartDate.add(applicant.options.timeLagInterval, applicant?.options?.timeLagIntervalUnit || 'seconds');
	}
	return sessionStartDate;
}

export function getSessionEnd(session, applicant) {
	let sessionEndDate = session.dates && moment.utc(session.dates.end);
	let sessionDurationInMs = 0;

	applicant?.log && console.log('Session end date', sessionEndDate?.format());
	applicant?.log && console.log('Session duration (config)', getSessionOption(session, 'duration'));

	// Session duration calculation
	if(getSessionOption(session, 'duration')) {
		sessionDurationInMs = parseInt(getSessionOption(session, 'duration'), 10) * 60 * 1000;
	} else if(session.dates && session.dates.start && session.dates.end) {
		sessionDurationInMs = moment.utc(session.dates.end).diff(moment.utc(session.dates.start));
	}

	// Session end date calculation
	if((getSessionOption(session, 'duration') || getSessionOption(session, 'isSlidingSchedule')) && getApplicantEvent(applicant, 'access', 'first')) {
		applicant?.log && console.log('sliding schedule:: adding session', sessionDurationInMs / 1000 / 60, 'minutes');
		sessionEndDate = moment.utc(getApplicantEvent(applicant, 'access', 'first')?.date).add(sessionDurationInMs / 1000, 'seconds');
	}

	applicant?.log && console.log('Session duration', sessionDurationInMs / 1000 / 60, 'minutes');
	if(applicant && getApplicantOption(applicant, 'supTime')) {
		let supTime = 0;
		switch(getApplicantOption(applicant, 'supTime')) {
		case '1/4':
			supTime = sessionDurationInMs / 4;
			break;

		case '1/3':
			supTime = sessionDurationInMs / 3;
			break;

		default:
			supTime = (sessionDurationInMs * parseFloat(`${getApplicantOption(applicant, 'supTime')}`.replace(',', '.'))) / 100;
			break;
		}

		applicant?.log && console.log('User supTime => add ', supTime / 1000 / 60 / 60, 'heures');
		applicant?.log && console.log('Normally end at', sessionEndDate.toString());
		sessionEndDate.add(supTime / 1000, 'seconds');
		// console.log('Finnaly end at', sessionEndDate.toString());
	}
	applicant?.log && console.log('Session ends ', sessionEndDate.format());
	return sessionEndDate;
}

export function isSessionActive(session, applicant = null) {
	let end = getSessionEnd(session, applicant);
	if(session.dates && session.dates.endAccess && (applicant && !getApplicantEvent(applicant, 'access', 'first'))) {
		end = moment.utc(session.dates.endAccess);
	}
	applicant?.log && console.log(
		'isSessionActive::: end date',
		{
			// status: session.status,
			// statusCheck: session.status === SESSION_STATUS_ONPROGRESS,
			// sessionDates: session.dates,
			sessionStart: (session.dates.identification || session.dates.start),
			sessionEnd: session.dates.end,
			applicantStart: getSessionStart(session, applicant).format(),
			applicantEnd: end?.format(),
			now: moment.utc().format(),
			isStartBefore: getSessionStart(session, applicant).isSameOrBefore(moment.utc()),
			isEndAfter: end.isSameOrAfter(moment.utc()),
			allowTimeout: getSessionOption(session, 'allowTimeout') ? getApplicantEvent(applicant, 'access', 'first') : 'no timeout allowed',
		},
	);
	return session
        && session.status === SESSION_STATUS_ONPROGRESS
        && session.dates
        && (session.dates.identification || session.dates.start)
        && end
        && getSessionStart(session, applicant).isSameOrBefore(moment.utc())
        && (moment(end).isSameOrAfter(moment.utc()) || (getSessionOption(session, 'allowTimeout') && getApplicantEvent(applicant, 'access', 'first')));
}

export function isSessionUpcoming(session, applicant = null) {
	let end = getSessionEnd(session, applicant);
	if(session.dates && session.dates.endAccess && (applicant && !getApplicantEvent(applicant, 'access', 'first'))) {
		end = session.dates.endAccess || session.dates.end;
	}
	return session
        && session.status === SESSION_STATUS_ONPROGRESS
        && session.dates
        // && session.dates.start
        && end
        // && moment.utc()
        //     .add(12, 'hours')
        //     .isSameOrAfter(moment(session.dates.start))
        && moment.utc()
        	.isSameOrBefore(end);
}

export function getSessionNotationMax(session, applicants = null) {
	return session.notation?.max || (applicants || []).find(applicant => applicant.marks?.max)?.marks?.max || null;
}

export function getFinalMax(session, applicant) {
	if(session && applicant && applicant.selectedQuestions && applicant.selectedQuestions.questions) {
		const notation = session.notation && session.notation.type ? session.notation : { type: 'free', allowNegative: false };

		if(notation.max) {
			return parseFloat(notation.max);
		}

		return getPriceFromCent(getApplicantSelectedQuestions(session?.subjectData, applicant).reduce((total, question) => {
			return total + getPriceCent(question.points);
		}, 0));
	}
	return 0;
}

export function getFinalMark(session, applicant, realMark) {
	if(session && applicant && applicant.selectedQuestions && applicant.selectedQuestions.questions) {
		const notation = session.notation && session.notation.type ? session.notation : { type: 'free', allowNegative: false };
		const realMax = getApplicantSelectedQuestions(session?.subjectData, applicant).reduce((total, question) => {
			return total + question.points;
		}, 0);

		if(realMark < 0 && !notation.allowNegative) {
			realMark = 0;
		}

		switch(notation.type) {
		case 'ratio':
		case 'step':
		case 'binary':
			if(notation.max && realMax) {
				console.log('notation max', notation.max, 'result', realMark * notation.max / realMax);
				return Math.round((realMark * notation.max / realMax) * 100) / 100;
			}
			console.error('Missing data to calculate final mark', notation, realMax);
			break;

		case 'free':
		default:
			return realMark;
		}
	}
	return realMark;
}

// Export Actions
export function setSessions(sessions, isSet = true) {
	return {
		type: SET_SESSIONS,
		sessions,
		isSet,
	};
}

export function setSessionSubjectData(sessionId, subjectData) {
	return {
		type: SET_SESSION_SUBJECT_DATA,
		sessionId,
		subjectData,
	};
}

export function addActiveProctor(sessionId, proctorId) {
	return {
		type: ADD_ACTIVE_PROCTOR,
		sessionId,
		proctorId,
	};
}

export function removeActiveProctor(sessionId, proctorId) {
	return {
		type: REMOVE_ACTIVE_PROCTOR,
		sessionId,
		proctorId,
	};
}

export function removeSession(sessionId) {
	return {
		type: REMOVE_SESSION,
		sessionId,
	};
}
